// Account Sessions (admin only): who logged in to the CRM, on which device, and when.
// Data: GET /auth/sessions (Models/UserSession.js). All times in US Pacific.
// Tabs: By user (device count per user) | All sessions. Super admin: Logout all users (super admins are skipped).
import { useCallback, useEffect, useState } from 'react';
import { Badge, Button, Card, DatePicker, Modal, Select, Space, Table, Tabs, Tag, Tooltip, Typography, message } from 'antd';
import {
  DesktopOutlined,
  GoogleOutlined,
  KeyOutlined,
  LogoutOutlined,
  MobileOutlined,
  ReloadOutlined,
  TabletOutlined,
} from '@ant-design/icons';
import API from '../../API/API';
import { formatPT, zoneAbbrPT, toApiDays, disabledFuturePT } from '../../utils/usTime';
import { isSuperAdminUser } from '../../utils/roles';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const STATUS_TAGS = {
  active: { color: 'green', label: 'Active' },
  'logged-out': { color: 'default', label: 'Logged out' },
  'idle-logout': { color: 'orange', label: 'Logged out (idle)' },
  replaced: { color: 'default', label: 'Replaced (logged in again)' },
  'admin-logout': { color: 'red', label: 'Logged out by admin' },
  expired: { color: 'default', label: 'Expired' },
};

const DEVICE_ICONS = {
  mobile: <MobileOutlined />,
  tablet: <TabletOutlined />,
  desktop: <DesktopOutlined />,
};

// '5 min ago', '3 hr ago', '2 days ago'
const timeAgo = (value) => {
  const ms = Date.now() - new Date(value).getTime();
  if (!Number.isFinite(ms)) return '';
  const min = Math.floor(ms / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min} min ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} hr ago`;
  const days = Math.floor(hr / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
};

// 'Chrome 130 · Windows 11', 'Chrome 130 · Android 14 (SM-S918B)', 'Safari 17 · iOS 17'
const deviceText = (device = {}) => {
  const browser = [device.browser || 'Other', device.browserVersion].filter(Boolean).join(' ');
  const os = [device.os || 'Other', device.osVersion].filter(Boolean).join(' ');
  return `${browser} · ${os}${device.model ? ` (${device.model})` : ''}`;
};

const deviceCell = (device) => (
  <Space>
    <span style={{ fontSize: 18, color: '#4b5563' }}>{DEVICE_ICONS[device?.deviceType] || DEVICE_ICONS.desktop}</span>
    <Text>{deviceText(device)}</Text>
  </Space>
);

const onlineDot = (online) => (
  <Badge status={online ? 'success' : 'default'} text={online ? 'Online' : 'Idle'} />
);

const userCell = (user, isCurrent) => (
  <div>
    <Space size={6}>
      <Text strong>{user?.userName || 'Deleted user'}</Text>
      {isCurrent && <Tag color="blue" style={{ marginInlineEnd: 0 }}>Current</Tag>}
    </Space>
    <div style={{ fontSize: 12, color: '#6b7280' }}>
      {[user?.firstName, user?.lastName].filter(Boolean).join(' ')}
    </div>
    {user?.role && (
      <Tag style={{ marginTop: 4, fontSize: 11 }}>
        {user.role}{user.subRole ? ` | ${user.subRole}` : ''}
      </Tag>
    )}
  </div>
);

const ptDateTime = (value) => (value ? `${formatPT(value, 'MMM DD, YYYY hh:mm A')} ${zoneAbbrPT(new Date(value))}` : '—');

export default function AccountSessions() {
  const [tab, setTab] = useState('by-user');
  const [status, setStatus] = useState('active');
  const [userId, setUserId] = useState(undefined);
  const [range, setRange] = useState(null);
  const [users, setUsers] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loggingOutAll, setLoggingOutAll] = useState(false);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [loggingOutSelected, setLoggingOutSelected] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const canLogoutAll = isSuperAdminUser();
  const myUserId = (() => {
    try { return JSON.parse(localStorage.getItem('userData') || '{}')?.id || null; } catch { return null; }
  })();
  // Super admins and yourself can't be logged out from here (backend skips them too).
  const isProtectedUser = (user) =>
    !user || (user.role === 'ADMIN' && user.isSuperAdmin === true) || String(user._id) === String(myUserId);

  // Users for the filter
  useEffect(() => {
    (async () => {
      const res = await API.userGetAll();
      if (res?.success && Array.isArray(res.data)) {
        setUsers(
          res.data
            .filter((u) => !u.isDeleted)
            .map((u) => ({
              value: u._id,
              label: `${u.userName || ''} — ${[u.firstName, u.lastName].filter(Boolean).join(' ')} (${u.role})`,
            }))
        );
      }
    })();
  }, []);

  const loadSessions = useCallback(async (page = 1, pageSize = 20) => {
    setLoading(true);
    try {
      const [startDate, endDate] = toApiDays(range);
      const res = await API.getSessions({
        page,
        limit: pageSize,
        ...(tab === 'by-user' ? { view: 'by-user' } : { status }),
        ...(userId ? { userId } : {}),
        ...(startDate ? { startDate, endDate } : {}),
      });
      if (res?.success) {
        setRows(res.data || []);
        setPagination({ current: res.pagination.page, pageSize: res.pagination.limit, total: res.pagination.total });
      } else {
        message.error(res?.message || 'Failed to load sessions');
      }
    } catch (err) {
      console.error('loadSessions error', err);
      message.error('Failed to load sessions');
    } finally {
      setLoading(false);
    }
  }, [tab, status, userId, range]);

  useEffect(() => {
    setRows([]);
    setSelectedUserIds([]);
    loadSessions(1, pagination.pageSize);
    // Reload from page 1 whenever the tab or a filter changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, status, userId, range]);

  const confirmLogoutAll = () => {
    Modal.confirm({
      title: 'Log out all users?',
      icon: <LogoutOutlined style={{ color: '#ff4d4f' }} />,
      content: (
        <div>
          <p>Every user except super admins will be logged out on all devices and must log in again.</p>
          <p style={{ color: '#cf1322', marginBottom: 0 }}>
            Online agents are set offline and will not receive leads until they log in and go online again.
          </p>
        </div>
      ),
      okText: 'Yes, log out all',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        setLoggingOutAll(true);
        try {
          const res = await API.logoutAllUsers();
          if (res?.success) {
            message.success(`Logged out ${res.usersAffected} users (${res.usersSetOffline} set offline). Super admins skipped: ${res.superAdminsSkipped ?? 0}.`);
            setSelectedUserIds([]);
            loadSessions(1, pagination.pageSize);
          } else {
            message.error(res?.message || 'Failed to log out users');
          }
        } finally {
          setLoggingOutAll(false);
        }
      },
    });
  };

  const confirmLogoutSelected = () => {
    const picked = rows.filter((r) => selectedUserIds.includes(r._id));
    const names = picked.map((r) => r.user?.userName || 'user');
    Modal.confirm({
      title: `Log out ${picked.length} selected user${picked.length === 1 ? '' : 's'}?`,
      icon: <LogoutOutlined style={{ color: '#ff4d4f' }} />,
      content: (
        <div>
          <p style={{ marginBottom: 8 }}>
            {names.slice(0, 10).join(', ')}{names.length > 10 ? ` and ${names.length - 10} more` : ''}
          </p>
          <p>They will be logged out on all devices and must log in again.</p>
          <p style={{ color: '#cf1322', marginBottom: 0 }}>
            Online agents are set offline and will not receive leads until they log in and go online again.
          </p>
        </div>
      ),
      okText: 'Yes, log out selected',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        setLoggingOutSelected(true);
        try {
          const res = await API.logoutSelectedUsers(picked.map((r) => String(r._id)));
          if (res?.success) {
            message.success(`Logged out ${res.usersAffected} user${res.usersAffected === 1 ? '' : 's'} (${res.usersSetOffline} set offline).`);
            setSelectedUserIds([]);
            loadSessions(pagination.current, pagination.pageSize);
          } else {
            message.error(res?.message || 'Failed to log out users');
          }
        } finally {
          setLoggingOutSelected(false);
        }
      },
    });
  };

  const byUserColumns = [
    {
      title: 'User',
      key: 'user',
      width: 260,
      render: (_, r) => userCell(r.user, r.devices?.some((d) => d.isCurrent)),
    },
    {
      title: 'Devices',
      key: 'devices',
      width: 200,
      render: (_, r) => (
        <div>
          <Text strong>{r.activeDevices} device{r.activeDevices === 1 ? '' : 's'}</Text>
          <div style={{ marginTop: 2 }}>
            <Badge status={r.onlineDevices > 0 ? 'success' : 'default'} text={`${r.onlineDevices} online`} />
          </div>
        </div>
      ),
    },
    {
      title: 'Device list',
      key: 'deviceList',
      render: (_, r) => (
        <Space direction="vertical" size={2}>
          {(r.devices || []).slice(0, 3).map((d) => (
            <Text key={d._id} style={{ fontSize: 13 }}>{deviceText(d.device)}</Text>
          ))}
          {(r.devices || []).length > 3 && <Text type="secondary" style={{ fontSize: 12 }}>+{r.devices.length - 3} more (expand)</Text>}
        </Space>
      ),
    },
    {
      title: 'Last Active',
      dataIndex: 'lastActiveAt',
      key: 'lastActiveAt',
      width: 220,
      render: (v) => (
        <div>
          <div>{ptDateTime(v)}</div>
          {v && <div style={{ fontSize: 12, color: '#6b7280' }}>{timeAgo(v)}</div>}
        </div>
      ),
    },
  ];

  const deviceDetailColumns = [
    { title: 'Device', key: 'device', render: (_, d) => deviceCell(d.device) },
    { title: 'IP Address', dataIndex: 'ip', key: 'ip', render: (ip) => <Text copyable={Boolean(ip)}>{ip || '—'}</Text> },
    {
      title: 'Login',
      dataIndex: 'loginMethod',
      key: 'loginMethod',
      render: (m) => (m === 'google'
        ? <Tag icon={<GoogleOutlined />} color="geekblue">Google</Tag>
        : <Tag icon={<KeyOutlined />}>Password</Tag>),
    },
    { title: 'Logged In', dataIndex: 'createdAt', key: 'createdAt', render: (v) => ptDateTime(v) },
    {
      title: 'Last Active',
      dataIndex: 'lastActiveAt',
      key: 'lastActiveAt',
      render: (v, d) => (
        <div>
          <div>{ptDateTime(v)}</div>
          <Space size={8} style={{ fontSize: 12, color: '#6b7280' }}>
            <span>{timeAgo(v)}</span>
            {onlineDot(d.isOnline)}
            {d.isCurrent && <Tag color="blue" style={{ marginInlineEnd: 0 }}>Current</Tag>}
          </Space>
        </div>
      ),
    },
  ];

  const sessionColumns = [
    {
      title: 'User',
      key: 'user',
      width: 240,
      render: (_, r) => userCell(r.user, r.isCurrent),
    },
    {
      title: 'Device',
      key: 'device',
      width: 260,
      render: (_, r) => deviceCell(r.device),
    },
    {
      title: 'IP Address',
      dataIndex: 'ip',
      key: 'ip',
      width: 150,
      render: (ip) => <Text copyable={Boolean(ip)}>{ip || '—'}</Text>,
    },
    {
      title: 'Login',
      dataIndex: 'loginMethod',
      key: 'loginMethod',
      width: 110,
      render: (m) => (m === 'google'
        ? <Tag icon={<GoogleOutlined />} color="geekblue">Google</Tag>
        : <Tag icon={<KeyOutlined />}>Password</Tag>),
    },
    {
      title: 'Logged In',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 200,
      render: (v) => ptDateTime(v),
    },
    {
      title: 'Last Active',
      dataIndex: 'lastActiveAt',
      key: 'lastActiveAt',
      width: 200,
      render: (v) => (
        <div>
          <div>{ptDateTime(v)}</div>
          {v && <div style={{ fontSize: 12, color: '#6b7280' }}>{timeAgo(v)}</div>}
        </div>
      ),
    },
    {
      title: 'Status',
      key: 'status',
      width: 190,
      render: (_, r) => {
        const s = STATUS_TAGS[r.status] || STATUS_TAGS.active;
        return (
          <div>
            <Tag color={s.color}>{s.label}</Tag>
            {r.status === 'active' && (
              <div style={{ marginTop: 4 }}>{onlineDot(r.isOnline)}</div>
            )}
            {r.loggedOutAt && (
              <Tooltip title="Ended at">
                <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>{ptDateTime(r.loggedOutAt)}</div>
              </Tooltip>
            )}
          </div>
        );
      },
    },
  ];

  const tablePagination = {
    ...pagination,
    showSizeChanger: true,
    pageSizeOptions: [20, 50, 100],
    showTotal: (total) => (tab === 'by-user'
      ? `${total} user${total === 1 ? '' : 's'} logged in`
      : `${total} session${total === 1 ? '' : 's'}`),
  };

  return (
    <div style={{ padding: 24 }}>
      <Card bordered={false} style={{ borderRadius: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16, marginBottom: 12 }}>
          <div>
            <Title level={3} style={{ margin: 0 }}>Account Sessions</Title>
            <Text type="secondary">Who logged in to the CRM, on which device (times in US Pacific)</Text>
          </div>
          <Space wrap>
            {tab === 'all' && (
              <Select
                value={status}
                onChange={setStatus}
                style={{ width: 150 }}
                options={[
                  { value: 'active', label: 'Active sessions' },
                  { value: 'all', label: 'All sessions' },
                ]}
              />
            )}
            <Select
              allowClear
              showSearch
              placeholder="All users"
              value={userId}
              onChange={setUserId}
              options={users}
              optionFilterProp="label"
              style={{ width: 280 }}
            />
            <RangePicker
              value={range}
              onChange={setRange}
              disabledDate={disabledFuturePT}
              format="MM/DD/YYYY"
              placeholder={['Login from', 'Login to']}
            />
            <Button icon={<ReloadOutlined />} onClick={() => loadSessions(pagination.current, pagination.pageSize)}>
              Refresh
            </Button>
            {canLogoutAll && tab === 'by-user' && selectedUserIds.length > 0 && (
              <Button danger icon={<LogoutOutlined />} loading={loggingOutSelected} onClick={confirmLogoutSelected}>
                Logout selected ({selectedUserIds.length})
              </Button>
            )}
            {canLogoutAll && (
              <Button danger type="primary" icon={<LogoutOutlined />} loading={loggingOutAll} onClick={confirmLogoutAll}>
                Logout all users
              </Button>
            )}
          </Space>
        </div>

        <Tabs
          activeKey={tab}
          onChange={setTab}
          items={[
            { key: 'by-user', label: 'By user' },
            { key: 'all', label: 'All sessions' },
          ]}
        />

        {tab === 'by-user' ? (
          <Table
            key="by-user"
            rowKey="_id"
            columns={byUserColumns}
            dataSource={rows}
            rowSelection={canLogoutAll ? {
              selectedRowKeys: selectedUserIds,
              onChange: (keys) => setSelectedUserIds(keys),
              getCheckboxProps: (r) => ({
                disabled: isProtectedUser(r.user),
                title: isProtectedUser(r.user) ? 'Super admins and you are not logged out' : undefined,
              }),
            } : undefined}
            loading={loading}
            scroll={{ x: 900 }}
            expandable={{
              expandedRowRender: (r) => (
                <Table
                  rowKey="_id"
                  size="small"
                  columns={deviceDetailColumns}
                  dataSource={r.devices || []}
                  pagination={false}
                />
              ),
            }}
            pagination={tablePagination}
            onChange={(p) => {
              setSelectedUserIds([]); // ticks only apply to the page you see
              loadSessions(p.current, p.pageSize);
            }}
          />
        ) : (
          <Table
            key="all"
            rowKey="_id"
            columns={sessionColumns}
            dataSource={rows}
            loading={loading}
            scroll={{ x: 1300 }}
            pagination={tablePagination}
            onChange={(p) => loadSessions(p.current, p.pageSize)}
          />
        )}
      </Card>
    </div>
  );
}
